import React from 'react';
import Grow from '@mui/material/Grow';
import { Stack } from '@cp/ds/src/components/stack';
import { FormField } from '@cp/ds/src/components/form';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Divider } from '@cp/ds/src/components/divider';
import { Typography } from '@cp/ds/src/components/typography';
import { KidIcon } from '@cp/ds/src/icons/kid';
import { LogoIcon } from '@cp/ds/src/icons/logo';
import { NurseIcon } from '@cp/ds/src/icons/career/common/nurse';
import { CloseIcon } from '@cp/ds/src/icons';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useConfig } from '@cp/shared/config';
import { useIsMobile } from '@cp/ds/src/hooks';
import { FieldTokens } from '@youhelp-ru/ui';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { MapPlaceholderWithPins } from 'src/shared/ui/map';
import { CatalogFormFieldName } from 'src/entities/catalog/model/formFieldName';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { FormFieldName } from '../model/form';
import { getFieldNames } from '../model/utils/getFieldsNames';
import PlaceholderUrl from '../../../../assets/shapes/map.svg?url';
import MapButton from '../../../../assets/shapes/mapButton.svg';
import { AvailableOnlyField } from '../../ui/availableOnly';
import { SORTING_ITEMS, SORTING_ITEMS_VACANCY } from '../../nav/model';
import { SortButton } from '../../ui/sortButton';
import { DateField } from './date';
import { CatalogFilterViewProps } from './model';
import { PriceField } from './price';
import { OptionsField } from './options';
import { AgeField } from './age';
import { RegularSpecialistField } from './permanent';

const ADVANTAGES_MAIN_FIELD_NAMES: FormFieldName[] = [
  FormFieldName.Training,
  FormFieldName.Baby,
  FormFieldName.ChildWithDisabilities,
  FormFieldName.Options,
];
const CHILDREN_COUNT_ITEMS = Array.from({ length: 9 }, (_, index) => {
  const value = index + 1;
  return {
    label: String(value),
    value,
  };
});

const MapWithPins = MapPlaceholderWithPins(Button, {
  blurSx: {
    '&:hover': {
      backdropFilter: 'none',
      backgroundColor: 'transparent',
    },
  },
});

export const View: React.FC<CatalogFilterViewProps> = ({
  careerId,
  catalogType,
  onMapButtonClick,
  sx,
  onResetFields,
  form,
  careerDictionary,
}) => {
  const theme = useTheme();
  const config = useConfig();
  const isMobile = useIsMobile();
  const serviceName = config.get('APP_NAME');
  const fieldsToRender = getFieldNames({ careerId, catalogType });
  const specialistNameGenitive = careerDictionary?.[careerId]?.translation.nameSingleGenitive ?? 'специалиста';
  const sortingItems = catalogType === CatalogItemType.Vacancy ? SORTING_ITEMS_VACANCY : SORTING_ITEMS;

  return (
    <Stack component="form" direction="column" gap={2.5} noValidate sx={mergeSx({ [FieldTokens.LabelColor]: theme.palette.gray }, sx)}>
      {catalogType === CatalogItemType.Resume && <AvailableOnlyField form={form} isMobile={isMobile} />}

      {!isMobile && <SortButton fieldName={CatalogFormFieldName.Sorters} sortingItems={sortingItems} />}

      <Grow appear={false} in={Boolean(onMapButtonClick)} unmountOnExit>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 160,
          }}
        >
          <MapWithPins
            onClick={onMapButtonClick}
            sx={{
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundImage: `url(${PlaceholderUrl})`,
              backgroundPosition: 'center center',
              width: '100%',
              height: 160,
              overflow: 'hidden',
              borderRadius: '16px',
            }}
            variant="text"
          >
            <Box sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 3 }}>
              <MapButton />
            </Box>
          </MapWithPins>

          <Button
            onClick={onMapButtonClick}
            size="small"
            sx={{
              position: 'absolute',
              backgroundColor: 'white',
              borderColor: theme.palette['gray light'],
              zIndex: 3,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
            variant="secondary"
          >
            Смотреть на карте
          </Button>
        </Box>
      </Grow>

      {fieldsToRender.includes(FormFieldName.Age) && <AgeField catalogType={catalogType} />}

      {fieldsToRender.includes(FormFieldName.Price) && <PriceField catalogType={catalogType} />}

      {fieldsToRender.includes(FormFieldName.DateStart) && <DateField />}

      {fieldsToRender.includes(FormFieldName.RegularSpecialist) && <RegularSpecialistField />}

      {fieldsToRender.includes(FormFieldName.ChildrenCount) && (
        <FormField
          fieldLabel="Количество детей"
          items={CHILDREN_COUNT_ITEMS}
          name={FormFieldName.ChildrenCount}
          size="small"
          sx={{ width: '100%' }}
          type="select"
        />
      )}

      {ADVANTAGES_MAIN_FIELD_NAMES.some((fieldName) => fieldsToRender.includes(fieldName)) && (
        <Stack direction="column" gap={1.5}>
          <Divider sx={{ mb: 1 }} />

          <Stack direction="row" sx={{ aligItems: 'center', justifyContent: 'space-between' }}>
            <Typography component="div" variant="button">
              Преимущества {isMobile && specialistNameGenitive}
            </Typography>
            {!isMobile && (
              <Button color="gray" onClick={() => onResetFields(fieldsToRender)} variant="text">
                <CloseIcon />
              </Button>
            )}
          </Stack>

          {fieldsToRender.includes(FormFieldName.Training) && (
            <FormField
              iconLeft={<LogoIcon sx={{ mr: 1 }} />}
              label={`Школа ${serviceName ?? 'нянь'}`}
              name={FormFieldName.Training}
              sx={{ width: 1, px: 1, backgroundColor: { md: theme.palette['gray ultra light'] } }}
              type="checkbox"
            />
          )}

          {fieldsToRender.includes(FormFieldName.Baby) && (
            <FormField
              iconLeft={<KidIcon sx={{ mr: 1 }} />}
              label="Сидит с младенцами"
              name={FormFieldName.Baby}
              sx={{ width: 1, px: 1, backgroundColor: { md: theme.palette['gray ultra light'] } }}
              type="checkbox"
            />
          )}

          {fieldsToRender.includes(FormFieldName.ChildWithDisabilities) && (
            <FormField
              iconLeft={<NurseIcon sx={{ mr: 1 }} />}
              label="Сидит с особыми детьми"
              name={FormFieldName.ChildWithDisabilities}
              sx={{ width: 1, px: 1, backgroundColor: { md: theme.palette['gray ultra light'] } }}
              type="checkbox"
            />
          )}
          {fieldsToRender.includes(FormFieldName.Options) && (
            <Stack direction="column" gap={1.5}>
              {fieldsToRender.includes(FormFieldName.Training) && (
                <Typography sx={{ color: theme.palette.gray }} variant="caption bold">
                  Дополнительные
                </Typography>
              )}
              <OptionsField careerId={careerId} />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
